import { Component, inject, signal, model, OnDestroy, input, effect, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CommonService } from '../../../services/common.service';
import { Lcca } from '../../../models/common/lcca.model';
import { Subject } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
    selector: 'app-lcca-autocomplete',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        AutoCompleteModule
    ],
    templateUrl: './lcca-autocomplete.component.html',
    styleUrl: './lcca-autocomplete.component.scss'
})
export class LccaAutocompleteComponent implements OnDestroy {
    private commonService = inject(CommonService);
    private destroy$ = new Subject<void>();

    // Two-way binding for the value (returns id as number)
    value = model<number | null>(null);

    // Output event when LCCA is selected - emits full object for parent to extract type/rate
    lccaSelected = output<Lcca | null>();

    // Input properties
    placeholder = input<string>('Search maintenance details...');
    minLength = 0;
    disabled = input<boolean>(false);

    // Internal state
    filteredLccas = signal<Lcca[]>([]);
    displayValue = signal<string>('');
    private lastFetchedId: number | null = null;

    constructor() {
        // Watch for value changes and fetch name when ID is set
        effect(() => {
            const currentValue = this.value();
            if (typeof currentValue === 'number' && currentValue > 0 && currentValue !== this.lastFetchedId) {
                this.lastFetchedId = currentValue;
                this.fetchNameById(currentValue);
            } else if (currentValue === null || currentValue === undefined) {
                this.displayValue.set('');
                this.lastFetchedId = null;
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private fetchNameById(id: number): void {
        this.commonService.getLccaById(id)
            .pipe(
                takeUntil(this.destroy$),
                catchError(() => of(null))
            )
            .subscribe((result) => {
                if (result && result.maintenance_detail) {
                    this.displayValue.set(result.maintenance_detail);
                }
            });
    }

    // Search LCCAs for autocomplete
    searchLccas(event: { query: string }): void {
        const query = event.query || '';

        // Allow empty query if minLength is 0
        if (query.length < this.minLength) {
            this.filteredLccas.set([]);
            return;
        }

        this.commonService.findLccas(1, 50, query)
            .pipe(
                takeUntil(this.destroy$),
                catchError(() => {
                    this.filteredLccas.set([]);
                    return of({ data: [], total: 0, page: 1 });
                })
            )
            .subscribe((result) => {
                this.filteredLccas.set(result.data || []);
            });
    }

    // Handle model change - update display value when user is typing
    onModelChange(value: any): void {
        if (typeof value === 'string') {
            this.displayValue.set(value);
        }
    }

    // Handle LCCA selection - returns the id and emits full object
    onLccaSelect(event: { value: Lcca }): void {
        const lcca = event.value;
        const lccaId = lcca.id ?? (lcca as any).ID;
        const lccaName = lcca.maintenance_detail || '';
        this.value.set(lccaId);
        this.displayValue.set(lccaName);
        this.lastFetchedId = lccaId;

        // Emit full LCCA object for parent to extract type and rate
        this.lccaSelected.emit(lcca);
    }

    // Handle clear
    onClear(): void {
        this.value.set(null);
        this.displayValue.set('');
        this.lastFetchedId = null;
        this.lccaSelected.emit(null);
    }
}
